import React from 'react';

import type { CardProps } from '@mui/material';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { filter, isEmpty, map, reduce, size } from 'lodash';
import { formatDate } from '../../../utils/libs';
import { ExportJobHiringsExcelDialog } from './export_job_hirings_excel_dialog';
import GroupJobsCard from './group_jobs_card';
import { JobCardItem } from './job_card_item';
import useLazyQueryDateJobs from './use_lazy_query_date_jobs';

import type { Job } from '../../../graphql/graphql';

export type DateJobsCardProps = {
  jobs: Job[];
  restaurantId: string;
  date: string;
  showPeriodJob?: boolean;
} & CardProps;

const DateJobsCard = React.memo(
  React.forwardRef<HTMLDivElement, DateJobsCardProps>(
    ({ jobs, restaurantId, date, showPeriodJob, ...CardProps }, ref) => {
      const [fetchData, { data, loading }] = useLazyQueryDateJobs({
        queryOptions: {
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: true,
        },
      });

      const jobsInternal = React.useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
        const _jobs = data?.jobs ?? jobs;
        if (showPeriodJob) {
          return _jobs;
        }
        return filter(_jobs, (job) => !job.periodJob);
      }, [jobs, data?.jobs, showPeriodJob]);
      const jobsPositionCountMap = React.useMemo(
        () =>
          reduce(
            jobsInternal,
            (prev, job) => {
              const hirings = filter(
                job.jobHirings,
                (jobHiring) => jobHiring.status === 'match' || jobHiring.status === 'done',
              );
              const applicants = filter(job.jobApplicants, (jobApplicant) => jobApplicant.status === 'open');

              return { ...prev, [job.id]: { applicantCount: size(applicants), hiringCount: size(hirings) } };
            },
            {} as { [key: string]: { applicantCount: number; hiringCount: number } },
          ),
        [jobsInternal],
      );

      const [openExportExcelDialog, setOpenExportExcelDialog] = React.useState(false);
      const openExportExcelHandler = () => {
        setOpenExportExcelDialog(true);
      };

      const refetchHandler = React.useCallback(async () => {
        if (restaurantId && date) {
          await fetchData({
            variables: {
              restaurantId,
              date: dayjs(date).format('YYYY-MM-DD'),
            },
          });
        }
      }, [fetchData, restaurantId, date]);

      return (
        <>
          <GroupJobsCard
            ref={ref}
            title={formatDate(date, 'ddd DD MMM')}
            loading={loading}
            isEmpty={isEmpty(jobsInternal)}
            onRefresh={refetchHandler}
            onExportExcel={openExportExcelHandler}
            {...CardProps}
          >
            <Stack gap={1}>
              {map(jobsInternal, (job) => (
                <JobCardItem
                  key={job.id}
                  job={job}
                  applicantCount={jobsPositionCountMap[job.id].applicantCount}
                  hiringCount={jobsPositionCountMap[job.id].hiringCount}
                />
              ))}
            </Stack>
          </GroupJobsCard>

          <ExportJobHiringsExcelDialog
            open={openExportExcelDialog}
            date={date}
            restaurantId={restaurantId}
            onClose={() => setOpenExportExcelDialog(false)}
          />
        </>
      );
    },
  ),
);

export default DateJobsCard;
